import { formatDate, getEffectiveDate } from "@/lib/dateUtils";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import type { GameMode } from "@/contexts/GameModeContext";

/**
 * Normalize persistence invariants across modes at boot:
 * - In practice/custom: if status/completion exists but date missing, stamp today
 * - In daily: if status is won/lost or completion=true without date, clear both;
 *             if status is playing and date missing, stamp today
 */
export function normalizeGamePersistence(): void {
  try {
    const modes: GameMode[] = ["daily", "practice", "custom"];
    const today = formatDate(getEffectiveDate());

    modes.forEach((mode) => {
      const status = modeAwareStorage.getCookieForMode("game_status", mode);
      const completion = modeAwareStorage.getCookieForMode("game_completion_state", mode);
      const savedDate = modeAwareStorage.getCookieForMode("game_persistence_date", mode);

      console.log(`[normalizePersistence] Checking mode=${mode}`, { status, completion, savedDate, today });

      // If DAILY has a saved date from a different day (including debug date), clear completion/status
      if (mode === "daily" && savedDate && savedDate !== today) {
        console.log(`[normalizePersistence] Daily stale date detected -> clearing`, { savedDate, today, status, completion });
        if (status === "won" || status === "lost" || completion === "true") {
          modeAwareStorage.removeCookieForMode("game_status", mode);
          modeAwareStorage.removeCookieForMode("game_completion_state", mode);
        }
        // Drop stale dates so the app can initialize cleanly for the new day
        modeAwareStorage.removeCookieForMode("game_persistence_date", mode);
        modeAwareStorage.removeCookieForMode("last_played", mode);
        return; // proceed to next mode
      }

      // If no date exists, normalize per mode
      if (!savedDate) {
        if (mode === "daily") {
          if (status === "won" || status === "lost" || completion === "true") {
            // Safer to clear than to incorrectly stamp today's date for an old completion
            modeAwareStorage.removeCookieForMode("game_status", mode);
            modeAwareStorage.removeCookieForMode("game_completion_state", mode);
          } else if (status === "playing") {
            // Playing state can safely be stamped for today
            modeAwareStorage.setCookieForMode("game_persistence_date", today, mode);
            modeAwareStorage.setCookieForMode("last_played", today, mode);
          }
        } else {
          // Practice/custom are not day-gated: stamp date when any relevant state exists
          if (status || completion === "true") {
            modeAwareStorage.setCookieForMode("game_persistence_date", today, mode);
            modeAwareStorage.setCookieForMode("last_played", today, mode);
          }
        }
      }
    });
  } catch (error) {
    console.error("[normalizeGamePersistence] Failed to normalize game persistence:", error);
  }
}
